import { FieldValues, useController, UseControllerProps } from "react-hook-form";
import { DatePicker, DatePickerProps } from "@airhelp/react";
import { handleFieldChange } from "./handleFieldChange";
import { useAppSettingsSnapshot } from "v2/settings";
import { mapLocaleToDatePickerLocale } from "app-settings/localesAndCurrencies";
import { useQuery } from "@tanstack/react-query";
import { Box } from "@chakra-ui/react";
import { isRtl } from "utils/getLocaleDirection";

type ControlledDatepickerProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    Omit<DatePickerProps, "onChange"> & {
      handleChange?: (date: Date) => void;
    };

export function ControlledDatepicker<T extends FieldValues>({
  label,
  placeholder,
  maxDate,
  name,
  rules,
  defaultValue,
  control,
  handleChange,
  ...inputProps
}: ControlledDatepickerProps<T>) {
  const { field } = useController({
    name,
    rules,
    defaultValue,
    control,
  });

  const { locale } = useAppSettingsSnapshot();
  const { data: datePickerLocale } = useQuery({
    queryKey: ["datePickerLocale", { locale }],
    queryFn: () => mapLocaleToDatePickerLocale(locale),
  });
  return (
    <Box
      css={{
        ".header-navigation button:nth-child(odd)": {
          transform: isRtl(locale) ? "rotate(180deg)" : "",
        },
      }}
    >
      <DatePicker
        {...inputProps}
        label={label}
        maxDate={maxDate}
        value={field.value}
        data-testid="datePicker"
        placeholder={placeholder}
        onChange={(date: Date) => {
          handleFieldChange(field, date);
          handleChange && handleChange(date);
        }}
        locale={datePickerLocale}
        isReadOnly
      />
    </Box>
  );
}
